import React, { useEffect, useRef } from 'react';
import { Modal, Flex } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import SText from '@/styles/SText';
import { ItemBarcode } from '@/types/inventoryV2';
import useDataList from '@/hooks/useDataList';
import { inventoryBarcodes, locationBarcodes } from '@/apis/inventoryv2';
import { CommonListParams } from '@/types/common';
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from '@/styles/STableLayout';
import { AnyObject } from 'antd/es/_util/type';
import dayjs from 'dayjs';
import { dateFormat } from '@/utils/format';
import { getUserName } from '@/utils/user';

interface BarcodeListProps {
  id: string;
  type: 'inventory' | 'location';
  title?: string;
  onClose: () => void;
}

const BarcodeList: React.FC<BarcodeListProps> = ({
  id,
  type,
  title,
  onClose
}) => {
  const { t } = useTranslation();
  const tableRef = useRef<TableLayoutRef | null>(null);

  // Choose API based on type
  const fetchApi = type === 'inventory' ? inventoryBarcodes : locationBarcodes;
  const [list, total, loading, { fetchList }] = useDataList<ItemBarcode, [string, CommonListParams]>({
    fetchApi
  });

  useEffect(() => {
    if (id) {
      tableRef?.current?.resetTablePage();
      fetch(0);
    }
  }, [id]);

  const fetch = (offset: number) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    const params: CommonListParams = {
      pagination: { offset, take },
    }
    if (id) {
      fetchList(id, params);
    }
  };

  const columns: ColumnsType<ItemBarcode> = [
    {
      title: t('warehouse.code'),
      dataIndex: 'barcode',
      key: 'barcode',
      width: 200,
      render: (text) => <SText text={text || ''} />
    },
    {
      title: t('purchase.productInfo'),
      children: [
        {
          title: <SText text={t('product.code')} />,
          key: 'productCode',
          width: 100,
          render: (_, record) => <SText text={record.product?.code || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('product.name')} />,
          key: 'productName',
          width: 120,
          render: (_, record) => <SText text={record.product?.name || ''} style={{ width: "100%" }} />
        },
      ]
    },
    {
      title: t('warehouse.batchNo'),
      key: 'batchNo',
      width: 150,
      render: (_, record) => <SText text={record.lotItemLocation?.lot?.lot || ''} />
    },
    {
      title: t('warehouse.state'),
      dataIndex: 'state',
      key: 'state',
      width: 100,
      render: (text) => <SText text={text || ''} />
    },
    {
      title: t('common.createTime'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (text) => text ? dateFormat(dayjs(text)) : ''
    },
    {
      title: t('common.creator'),
      key: 'creator',
      width: 100,
      render: (_, record) => <SText text={record.creator ? getUserName(record.creator) : ''} />
    },
    {
      title: t('common.updateTime'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 150,
      render: (text) => text ? dateFormat(dayjs(text)) : ''
    },
    {
      title: t('common.updater'),
      key: 'updater',
      width: 100,
      render: (_, record) => <SText text={record.updater ? getUserName(record.updater) : ''} />
    },
  ];

  return (
    <Modal
      title={title || t('warehouse.barcodeList')}
      open={!!id}
      onCancel={onClose}
      footer={null}
      width={1200}
      destroyOnClose
    >
      <Flex vertical style={{ height: '70vh' }}>
        <STableLayout
          headerChildren={null}
          tableLayoutRef={tableRef}
          total={total}
          handleChange={(offset) => { fetch(offset) }}
          columns={columns as ColumnsType<AnyObject>}
          dataSource={list}
          loading={loading}
          scrollX={1400}
        />
      </Flex>
    </Modal>
  );
};

export default BarcodeList;